import { useState, useCallback } from 'react';
import { Building, Floor, Apartment } from '../types/building';
import { supabase } from '../lib/supabase';
import { 
  getBuilding, 
  updateBuilding, 
  updateApartment as updateLocalApartment,
  createBuilding,
  getCurrentUser
} from '../lib/localDatabase';
import { formatApartmentNumber } from '../utils/formatting';
import { isOfflineModeEnabled, setOfflineMode } from '../lib/offlineStorage';
import { addAuditLog } from '../lib/auditLog';

export const useLocalBuilding = (initialBuilding?: Building) => {
  const [building, setBuilding] = useState<Building>(() => {
    if (initialBuilding) {
      return initialBuilding;
    }

    // Get current user for organization ID
    const currentUser = getCurrentUser();
    
    // Create new building
    return {
      id: crypto.randomUUID(),
      organizationId: currentUser?.organizationId || 'default-org',
      name: 'Neues Gebäude',
      address: 'Musterstraße 1, 12345 Musterstadt',
      floors: [],
      metadata: {
        status: 'planning',
        startDate: new Date()
      }
    };
  });

  const loadBuilding = useCallback(async (id: string) => {
    // Handle 'new' building case
    if (id === 'new') {
      return building;
    }

    // If offline mode is enabled, use local storage
    if (isOfflineModeEnabled()) {
      const loadedBuilding = getBuilding(id);
      if (loadedBuilding) {
        setBuilding(loadedBuilding);
        return loadedBuilding;
      }
      return null;
    }
    
    // Otherwise, fetch from Supabase
    try {
      const { data, error } = await supabase
        .from('buildings')
        .select(`
          id,
          name,
          address,
          organization_id,
          created_at,
          updated_at,
          created_by,
          archived_at,
          metadata,
          floors (
            id,
            number,
            custom_fields,
            apartments (
              id,
              number,
              formatted_number,
              status,
              notes,
              area_type,
              abort_reason,
              technician,
              konzern_id,
              last_updated,
              custom_fields,
              history
            )
          )
        `)
        .eq('id', id)
        .maybeSingle(); // Use maybeSingle instead of single to handle not found case
        
      if (error) {
        console.error('Error fetching building:', error);
        
        // Fallback to local storage
        const loadedBuilding = getBuilding(id);
        if (loadedBuilding) {
          setBuilding(loadedBuilding);
          return loadedBuilding;
        }
        
        throw error;
      }

      // If no data found, return null
      if (!data) {
        return null;
      }
      
      // Transform data to match our Building type
      const transformedBuilding: Building = {
        id: data.id,
        organizationId: data.organization_id,
        name: data.name,
        address: data.address,
        floors: (data.floors || []).map(floor => ({
          id: floor.id,
          number: floor.number,
          apartments: (floor.apartments || []).map(apt => ({
            id: apt.id,
            number: apt.number,
            formattedNumber: apt.formatted_number,
            status: apt.status as any,
            notes: apt.notes || '',
            areaType: apt.area_type as any,
            abortReason: apt.abort_reason,
            technician: apt.technician,
            konzernId: apt.konzern_id,
            lastUpdated: apt.last_updated ? new Date(apt.last_updated) : undefined,
            customFields: apt.custom_fields,
            history: apt.history
          })),
          customFields: floor.custom_fields
        })),
        archivedAt: data.archived_at ? new Date(data.archived_at) : undefined,
        customFields: data.metadata?.customFields,
        metadata: data.metadata
      };
      
      setBuilding(transformedBuilding);
      return transformedBuilding;
    } catch (error) {
      console.error('Error loading building:', error);
      throw error;
    }
  }, [building]);

  const createNewBuilding = useCallback(async (name: string, address: string) => {
    try {
      // If offline mode is enabled, use local storage
      if (isOfflineModeEnabled()) {
        const newBuilding = createBuilding(name, address);
        setBuilding(newBuilding);
        return newBuilding;
      }
      
      // Otherwise, create in Supabase
      const currentUser = getCurrentUser();
      
      // Insert new building
      const { data: insertedData, error: insertError } = await supabase
        .from('buildings')
        .insert({
          name,
          address,
          organization_id: currentUser?.organizationId || 'default-org',
          created_by: currentUser?.id,
          metadata: {
            status: 'planning',
            startDate: new Date().toISOString()
          }
        })
        .select('id')
        .single();
        
      if (insertError) {
        console.error('Error creating building:', insertError);
        
        // If we get a permission error, switch to offline mode
        if (insertError.code === '42501') {
          console.log('Permission error, switching to offline mode');
          setOfflineMode(true);
          
          // Create building in local storage
          const newBuilding = createBuilding(name, address);
          setBuilding(newBuilding);
          return newBuilding;
        }
        
        throw insertError;
      }

      // Fetch the newly created building to get all fields
      const { data, error } = await supabase
        .from('buildings')
        .select(`
          id,
          name,
          address,
          organization_id,
          created_at,
          updated_at,
          created_by,
          archived_at,
          metadata
        `)
        .eq('id', insertedData.id)
        .maybeSingle();

      if (error || !data) {
        console.error('Error fetching new building:', error);
        throw error || new Error('Building not found after creation');
      }
      
      // Create building object
      const newBuilding: Building = {
        id: data.id,
        organizationId: data.organization_id,
        name: data.name,
        address: data.address,
        floors: [],
        metadata: data.metadata
      };
      
      setBuilding(newBuilding);
      
      // Log the creation
      addAuditLog('create', 'building', `Building created: ${name}`, data.id);
      
      return newBuilding;
    } catch (error) {
      console.error('Error creating new building:', error);
      
      // If there's an error, switch to offline mode and create locally
      setOfflineMode(true);
      const newBuilding = createBuilding(name, address);
      setBuilding(newBuilding);
      return newBuilding;
    }
  }, []);

  const updateApartment = useCallback(async (
    floorId: string,
    apartmentId: string,
    updates: Partial<Apartment>
  ) => {
    if (!building.id) {
      throw new Error('Building ID is missing');
    }
    
    try {
      // If offline mode is enabled, use local storage
      if (isOfflineModeEnabled()) {
        const updatedApartment = updateLocalApartment(building.id, floorId, apartmentId, updates);
        
        // Update local state
        setBuilding(prev => ({
          ...prev,
          floors: prev.floors.map(floor => {
            if (floor.id !== floorId) return floor;
            return {
              ...floor,
              apartments: floor.apartments.map(apt => {
                if (apt.id !== apartmentId) return apt;
                return { 
                  ...apt, 
                  ...updates,
                  lastUpdated: new Date()
                };
              }),
            };
          }),
        }));
        
        return updatedApartment;
      }
      
      // Otherwise, update in Supabase
      try {
        const { data, error } = await supabase
          .from('apartments')
          .update({
            status: updates.status,
            notes: updates.notes,
            abort_reason: updates.abortReason,
            technician: updates.technician,
            konzern_id: updates.konzernId,
            last_updated: new Date().toISOString(),
            custom_fields: updates.customFields,
            history: updates.history
          })
          .eq('id', apartmentId)
          .select()
          .single();
          
        if (error) {
          console.error('Error updating apartment:', error);
          
          // If we get a permission error, switch to offline mode
          if (error.code === '42501') {
            console.log('Permission error, switching to offline mode');
            setOfflineMode(true);
            
            // Update in local storage
            const updatedApartment = updateLocalApartment(building.id, floorId, apartmentId, updates);
            
            // Update local state
            setBuilding(prev => ({
              ...prev,
              floors: prev.floors.map(floor => {
                if (floor.id !== floorId) return floor;
                return {
                  ...floor,
                  apartments: floor.apartments.map(apt => {
                    if (apt.id !== apartmentId) return apt;
                    return { 
                      ...apt, 
                      ...updates,
                      lastUpdated: new Date()
                    };
                  }),
                };
              }),
            }));
            
            return updatedApartment;
          }
          
          throw error;
        }
        
        // Update local state
        setBuilding(prev => ({
          ...prev,
          floors: prev.floors.map(floor => {
            if (floor.id !== floorId) return floor;
            return {
              ...floor,
              apartments: floor.apartments.map(apt => {
                if (apt.id !== apartmentId) return apt;
                return { 
                  ...apt, 
                  ...updates,
                  lastUpdated: new Date()
                };
              }),
            };
          }),
        }));
        
        // Log the update
        addAuditLog('update', 'apartment', `Apartment updated: ${data.formatted_number}`, apartmentId);
        
        return {
          id: data.id,
          number: data.number,
          formattedNumber: data.formatted_number,
          status: data.status as any,
          notes: data.notes || '',
          areaType: data.area_type as any,
          abortReason: data.abort_reason,
          technician: data.technician,
          konzernId: data.konzern_id,
          lastUpdated: data.last_updated ? new Date(data.last_updated) : undefined,
          customFields: data.custom_fields,
          history: data.history
        };
      } catch (error) {
        // If there's an error, switch to offline mode and update locally
        console.error('Error updating apartment in Supabase:', error);
        setOfflineMode(true);
        
        const updatedApartment = updateLocalApartment(building.id, floorId, apartmentId, updates);
        
        // Update local state
        setBuilding(prev => ({
          ...prev,
          floors: prev.floors.map(floor => {
            if (floor.id !== floorId) return floor;
            return {
              ...floor,
              apartments: floor.apartments.map(apt => {
                if (apt.id !== apartmentId) return apt;
                return { 
                  ...apt, 
                  ...updates,
                  lastUpdated: new Date()
                };
              }),
            };
          }),
        }));
        
        return updatedApartment;
      }
    } catch (error) {
      console.error('Error updating apartment:', error);
      throw error;
    }
  }, [building]);

  const updateBuildingInfo = useCallback(async (updates: Partial<Building>) => {
    try {
      console.log("Updating building with:", updates);
      
      // If offline mode is enabled, use local storage
      if (isOfflineModeEnabled()) {
        let updatedBuilding;
        
        // If we're updating an existing building
        if (building.id) {
          console.log("Updating existing building:", building.id);
          
          // Merge the current building with updates
          const mergedBuilding = {
            ...building,
            ...updates,
            // Preserve organization ID and floors if not provided
            organizationId: building.organizationId,
            floors: updates.floors || building.floors
          };
          
          // Update in local storage
          updatedBuilding = updateBuilding(building.id, mergedBuilding);
          setBuilding(updatedBuilding);
          
          console.log("Updated building:", updatedBuilding);
          return updatedBuilding;
        } 
        // If we're creating a new building
        else {
          console.log("Creating new building");
          
          // Ensure name and address are provided
          const name = updates.name || building.name;
          const address = updates.address || building.address;
          
          if (!name || !address) {
            throw new Error('Gebäudename und Adresse sind erforderlich');
          }
          
          // Create new building
          updatedBuilding = createBuilding(name, address);
          
          // If floors were provided, update the building with them
          if (updates.floors && updates.floors.length > 0) {
            console.log("Adding floors to new building:", updates.floors);
            updatedBuilding = updateBuilding(updatedBuilding.id, {
              ...updatedBuilding,
              floors: updates.floors
            });
          }
          
          setBuilding(updatedBuilding);
          
          console.log("Created building:", updatedBuilding);
          return updatedBuilding;
        }
      }
      
      // Otherwise, update in Supabase
      try {
        // If we're updating an existing building
        if (building.id) {
          // Update building
          const { error: buildingError } = await supabase
            .from('buildings')
            .update({
              name: updates.name || building.name,
              address: updates.address || building.address,
              updated_at: new Date().toISOString(),
              metadata: updates.metadata || building.metadata
            })
            .eq('id', building.id);
            
          if (buildingError) {
            console.error('Error updating building:', buildingError);
            
            // If we get a permission error, switch to offline mode
            if (buildingError.code === '42501') {
              console.log('Permission error, switching to offline mode');
              setOfflineMode(true);
              
              // Update in local storage
              const updatedBuilding = updateBuilding(building.id, {
                ...building,
                ...updates,
                floors: updates.floors || building.floors
              });
              
              setBuilding(updatedBuilding);
              return updatedBuilding;
            }
            
            throw buildingError;
          }
          
          // If floors were provided, update them
          if (updates.floors) {
            try {
              // For each floor, either create or update it
              for (const floor of updates.floors) {
                if (floor.id.startsWith('floor-')) {
                  // This is a new floor with a temporary ID, create it
                  const { data: floorData, error: floorError } = await supabase
                    .from('floors')
                    .insert({
                      building_id: building.id,
                      number: floor.number,
                      custom_fields: floor.customFields
                    })
                    .select()
                    .single();
                    
                  if (floorError) {
                    console.error('Error creating floor:', floorError);
                    
                    // If we get a permission error, switch to offline mode
                    if (floorError.code === '42501') {
                      console.log('Permission error, switching to offline mode');
                      setOfflineMode(true);
                      
                      // Update in local storage
                      const updatedBuilding = updateBuilding(building.id, {
                        ...building,
                        ...updates,
                        floors: updates.floors
                      });
                      
                      setBuilding(updatedBuilding);
                      return updatedBuilding;
                    }
                    
                    continue;
                  }
                  
                  // Create apartments for this floor
                  for (const apt of floor.apartments) {
                    const { error: apartmentError } = await supabase
                      .from('apartments')
                      .insert({
                        floor_id: floorData.id,
                        number: apt.number,
                        formatted_number: apt.formattedNumber || formatApartmentNumber(floor.number, apt.number),
                        status: apt.status,
                        notes: apt.notes,
                        area_type: apt.areaType,
                        abort_reason: apt.abortReason,
                        technician: apt.technician,
                        konzern_id: apt.konzernId,
                        last_updated: apt.lastUpdated?.toISOString(),
                        custom_fields: apt.customFields,
                        history: apt.history
                      });
                      
                    if (apartmentError) {
                      console.error('Error creating apartment:', apartmentError);
                      
                      // If we get a permission error, switch to offline mode
                      if (apartmentError.code === '42501') {
                        console.log('Permission error, switching to offline mode');
                        setOfflineMode(true);
                        
                        // Update in local storage
                        const updatedBuilding = updateBuilding(building.id, {
                          ...building,
                          ...updates,
                          floors: updates.floors
                        });
                        
                        setBuilding(updatedBuilding);
                        return updatedBuilding;
                      }
                    }
                  }
                } else {
                  // This is an existing floor, update it
                  const { error: floorError } = await supabase
                    .from('floors')
                    .update({
                      number: floor.number,
                      custom_fields: floor.customFields
                    })
                    .eq('id', floor.id);
                    
                  if (floorError) {
                    console.error('Error updating floor:', floorError);
                    
                    // If we get a permission error, switch to offline mode
                    if (floorError.code === '42501') {
                      console.log('Permission error, switching to offline mode');
                      setOfflineMode(true);
                      
                      // Update in local storage
                      const updatedBuilding = updateBuilding(building.id, {
                        ...building,
                        ...updates,
                        floors: updates.floors
                      });
                      
                      setBuilding(updatedBuilding);
                      return updatedBuilding;
                    }
                    
                    continue;
                  }
                  
                  // Update apartments for this floor
                  for (const apt of floor.apartments) {
                    if (apt.id.startsWith('apt-')) {
                      // This is a new apartment with a temporary ID, create it
                      const { error: apartmentError } = await supabase
                        .from('apartments')
                        .insert({
                          floor_id: floor.id,
                          number: apt.number,
                          formatted_number: apt.formattedNumber || formatApartmentNumber(floor.number, apt.number),
                          status: apt.status,
                          notes: apt.notes,
                          area_type: apt.areaType,
                          abort_reason: apt.abortReason,
                          technician: apt.technician,
                          konzern_id: apt.konzernId,
                          last_updated: apt.lastUpdated?.toISOString(),
                          custom_fields: apt.customFields,
                          history: apt.history
                        });
                        
                      if (apartmentError) {
                        console.error('Error creating apartment:', apartmentError);
                        
                        // If we get a permission error, switch to offline mode
                        if (apartmentError.code === '42501') {
                          console.log('Permission error, switching to offline mode');
                          setOfflineMode(true);
                          
                          // Update in local storage
                          const updatedBuilding = updateBuilding(building.id, {
                            ...building,
                            ...updates,
                            floors: updates.floors
                          });
                          
                          setBuilding(updatedBuilding);
                          return updatedBuilding;
                        }
                      }
                    } else {
                      // This is an existing apartment, update it
                      const { error: apartmentError } = await supabase
                        .from('apartments')
                        .update({
                          number: apt.number,
                          formatted_number: apt.formattedNumber || formatApartmentNumber(floor.number, apt.number),
                          status: apt.status,
                          notes: apt.notes,
                          area_type: apt.areaType,
                          abort_reason: apt.abortReason,
                          technician: apt.technician,
                          konzern_id: apt.konzernId,
                          last_updated: apt.lastUpdated?.toISOString(),
                          custom_fields: apt.customFields,
                          history: apt.history
                        })
                        .eq('id', apt.id);
                        
                      if (apartmentError) {
                        console.error('Error updating apartment:', apartmentError);
                        
                        // If we get a permission error, switch to offline mode
                        if (apartmentError.code === '42501') {
                          console.log('Permission error, switching to offline mode');
                          setOfflineMode(true);
                          
                          // Update in local storage
                          const updatedBuilding = updateBuilding(building.id, {
                            ...building,
                            ...updates,
                            floors: updates.floors
                          });
                          
                          setBuilding(updatedBuilding);
                          return updatedBuilding;
                        }
                      }
                    }
                  }
                }
              }
            } catch (error) {
              console.error('Error updating floors and apartments:', error);
              
              // Switch to offline mode and update locally
              setOfflineMode(true);
              
              const updatedBuilding = updateBuilding(building.id, {
                ...building,
                ...updates,
                floors: updates.floors
              });
              
              setBuilding(updatedBuilding);
              return updatedBuilding;
            }
          }
          
          // Reload building
          const updatedBuilding = await loadBuilding(building.id);
          
          // Log the update
          addAuditLog('update', 'building', `Building updated: ${updates.name || building.name}`, building.id);
          
          return updatedBuilding;
        } 
        // If we're creating a new building
        else {
          // Ensure name and address are provided
          const name = updates.name || building.name;
          const address = updates.address || building.address;
          
          if (!name || !address) {
            throw new Error('Gebäudename und Adresse sind erforderlich');
          }
          
          const currentUser = getCurrentUser();
          
          // Create building
          const { data: buildingData, error: buildingError } = await supabase
            .from('buildings')
            .insert({
              name,
              address,
              organization_id: currentUser?.organizationId || 'default-org',
              created_by: currentUser?.id,
              metadata: {
                status: 'planning',
                startDate: new Date().toISOString()
              }
            })
            .select()
            .single();
            
          if (buildingError) {
            console.error('Error creating building:', buildingError);
            
            // If we get a permission error, switch to offline mode
            if (buildingError.code === '42501') {
              console.log('Permission error, switching to offline mode');
              setOfflineMode(true);
              
              // Create building in local storage
              const newBuilding = createBuilding(name, address);
              
              // If floors were provided, update the building with them
              if (updates.floors && updates.floors.length > 0) {
                const updatedBuilding = updateBuilding(newBuilding.id, {
                  ...newBuilding,
                  floors: updates.floors
                });
                
                setBuilding(updatedBuilding);
                return updatedBuilding;
              }
              
              setBuilding(newBuilding);
              return newBuilding;
            }
            
            throw buildingError;
          }
          
          // If floors were provided, create them
          if (updates.floors && updates.floors.length > 0) {
            try {
              for (const floor of updates.floors) {
                const { data: floorData, error: floorError } = await supabase
                  .from('floors')
                  .insert({
                    building_id: buildingData.id,
                    number: floor.number,
                    custom_fields: floor.customFields
                  })
                  .select()
                  .single();
                  
                if (floorError) {
                  console.error('Error creating floor:', floorError);
                  
                  // If we get a permission error, switch to offline mode
                  if (floorError.code === '42501') {
                    console.log('Permission error, switching to offline mode');
                    setOfflineMode(true);
                    
                    // Create building in local storage
                    const newBuilding = createBuilding(name, address);
                    
                    // Update with floors
                    const updatedBuilding = updateBuilding(newBuilding.id, {
                      ...newBuilding,
                      floors: updates.floors
                    });
                    
                    setBuilding(updatedBuilding);
                    return updatedBuilding;
                  }
                  
                  continue;
                }
                
                // Create apartments
                for (const apt of floor.apartments) {
                  const { error: apartmentError } = await supabase
                    .from('apartments')
                    .insert({
                      floor_id: floorData.id,
                      number: apt.number,
                      formatted_number: apt.formattedNumber || formatApartmentNumber(floor.number, apt.number),
                      status: apt.status,
                      notes: apt.notes,
                      area_type: apt.areaType,
                      abort_reason: apt.abortReason,
                      technician: apt.technician,
                      konzern_id: apt.konzernId,
                      last_updated: apt.lastUpdated?.toISOString(),
                      custom_fields: apt.customFields,
                      history: apt.history
                    });
                    
                  if (apartmentError) {
                    console.error('Error creating apartment:', apartmentError);
                    
                    // If we get a permission error, switch to offline mode
                    if (apartmentError.code === '42501') {
                      console.log('Permission error, switching to offline mode');
                      setOfflineMode(true);
                      
                      // Create building in local storage
                      const newBuilding = createBuilding(name, address);
                      
                      // Update with floors
                      const updatedBuilding = updateBuilding(newBuilding.id, {
                        ...newBuilding,
                        floors: updates.floors
                      });
                      
                      setBuilding(updatedBuilding);
                      return updatedBuilding;
                    }
                  }
                }
              }
            } catch (error) {
              console.error('Error creating floors and apartments:', error);
              
              // Switch to offline mode and create locally
              setOfflineMode(true);
              
              const newBuilding = createBuilding(name, address);
              
              // Update with floors
              const updatedBuilding = updateBuilding(newBuilding.id, {
                ...newBuilding,
                floors: updates.floors
              });
              
              setBuilding(updatedBuilding);
              return updatedBuilding;
            }
          }
          
          // Create building object
          const newBuilding: Building = {
            id: buildingData.id,
            organizationId: buildingData.organization_id,
            name: buildingData.name,
            address: buildingData.address,
            floors: [],
            metadata: buildingData.metadata
          };
          
          // Reload building to get floors and apartments
          const loadedBuilding = await loadBuilding(buildingData.id);
          
          // Log the creation
          addAuditLog('create', 'building', `Building created: ${name}`, buildingData.id);
          
          return loadedBuilding || newBuilding;
        }
      } catch (error) {
        console.error('Error updating building in Supabase:', error);
        
        // Switch to offline mode and update locally
        setOfflineMode(true);
        
        if (building.id) {
          // Update existing building
          const updatedBuilding = updateBuilding(building.id, {
            ...building,
            ...updates,
            floors: updates.floors || building.floors
          });
          
          setBuilding(updatedBuilding);
          return updatedBuilding;
        } else {
          // Create new building
          const name = updates.name || building.name;
          const address = updates.address || building.address;
          
          if (!name || !address) {
            throw new Error('Gebäudename und Adresse sind erforderlich');
          }
          
          const newBuilding = createBuilding(name, address);
          
          // If floors were provided, update the building with them
          if (updates.floors && updates.floors.length > 0) {
            const updatedBuilding = updateBuilding(newBuilding.id, {
              ...newBuilding,
              floors: updates.floors
            });
            
            setBuilding(updatedBuilding);
            return updatedBuilding;
          }
          
          setBuilding(newBuilding);
          return newBuilding;
        }
      }
    } catch (error) {
      console.error('Error updating building:', error);
      
      // Switch to offline mode and update locally
      setOfflineMode(true);
      
      if (building.id) {
        // Update existing building
        const updatedBuilding = updateBuilding(building.id, {
          ...building,
          ...updates,
          floors: updates.floors || building.floors
        });
        
        setBuilding(updatedBuilding);
        return updatedBuilding;
      } else {
        // Create new building
        const name = updates.name || building.name;
        const address = updates.address || building.address;
        
        if (!name || !address) {
          throw new Error('Gebäudename und Adresse sind erforderlich');
        }
        
        const newBuilding = createBuilding(name, address);
        
        // If floors were provided, update the building with them
        if (updates.floors && updates.floors.length > 0) {
          const updatedBuilding = updateBuilding(newBuilding.id, {
            ...newBuilding,
            floors: updates.floors
          });
          
          setBuilding(updatedBuilding);
          return updatedBuilding;
        }
        
        setBuilding(newBuilding);
        return newBuilding;
      }
    }
  }, [building, loadBuilding]);

  return {
    building,
    loadBuilding,
    createNewBuilding,
    updateApartment,
    updateBuildingInfo
  };
};